import {
  BARTHEL_KEYS,
  BarthelItems,
  KognitifItems,
  NutrisiItems,
  RisikoJatuhItems,
  YaTidak
} from "@/types/asesmen_geriatri";

function num(v: unknown): number {
  if (v === null || v === undefined || v === "") return 0;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return isFinite(n) ? n : 0;
}

function terisi(v: unknown): boolean {
  return v !== null && v !== undefined && v !== "";
}

export function barthelTotal(items?: Partial<BarthelItems> | null): number {
  const src = items || {};
  return BARTHEL_KEYS.reduce((sum, key) => sum + num((src as any)[key]), 0);
}

export function barthelTerisi(items?: Partial<BarthelItems> | null): number {
  const src = items || {};
  return BARTHEL_KEYS.filter(key => terisi((src as any)[key])).length;
}

export function barthelLengkap(items?: Partial<BarthelItems> | null): boolean {
  return barthelTerisi(items) === BARTHEL_KEYS.length;
}

export function barthelKategori(total: number): string {
  if (total >= 100) return "Mandiri (100)";
  if (total >= 91) return "Ketergantungan Ringan (91–99)";
  if (total >= 61) return "Ketergantungan Sedang (61–90)";
  if (total >= 21) return "Ketergantungan Berat (21–60)";
  return "Ketergantungan Total (0–20)";
}

export function barthelColor(total: number): string {
  if (total >= 91) return "green";
  if (total >= 61) return "amber darken-2";
  return "red";
}

export const KOGNITIF_KEYS: (keyof KognitifItems)[] = [
  "orientasi",
  "registrasi",
  "atensi_kalkulasi",
  "recall",
  "bahasa_perintah"
];

export function kognitifTotal(items?: Partial<KognitifItems> | null): number {
  const src = items || {};
  return KOGNITIF_KEYS.reduce((sum, key) => sum + num((src as any)[key]), 0);
}

export function kognitifKategori(total: number): string {
  if (total >= 24) return "Normal (24–30)";
  if (total >= 17) return "Gangguan Kognitif Ringan (17–23)";
  return "Gangguan Kognitif Berat (0–16)";
}

export function kognitifColor(total: number): string {
  if (total >= 24) return "green";
  if (total >= 17) return "amber darken-2";
  return "red";
}

export const NUTRISI_KEYS: (keyof NutrisiItems)[] = [
  "asupan_makanan",
  "kehilangan_bb",
  "mobilitas",
  "psikologis",
  "neurologis",
  "imt",
  "lila"
];

export function nutrisiTotal(items?: Partial<NutrisiItems> | null): number {
  const src = items || {};
  const total = NUTRISI_KEYS.reduce((sum, key) => sum + num((src as any)[key]), 0);
  return Math.round(total * 10) / 10;
}

export function nutrisiInterpretasi(total: number): string {
  if (total >= 12) return "Status Gizi Normal (12–14)";
  if (total >= 8) return "Berisiko Malnutrisi (8–11)";
  return "Malnutrisi (0–7)";
}

export function nutrisiColor(total: number): string {
  if (total >= 12) return "green";
  if (total >= 8) return "amber darken-2";
  return "red";
}

export const RISIKO_JATUH_BINER_KEYS: (keyof RisikoJatuhItems)[] = [
  "riwayat_jatuh",
  "status_mental",
  "gangguan_penglihatan_berat",
  "kebiasaan_berkemih"
];

export const RISIKO_JATUH_MOBILITAS_KEYS: (keyof RisikoJatuhItems)[] = [
  "transfer",
  "mobilitas"
];

export const RISIKO_JATUH_KEYS: (keyof RisikoJatuhItems)[] = [
  ...RISIKO_JATUH_BINER_KEYS,
  ...RISIKO_JATUH_MOBILITAS_KEYS
];

export function risikoJatuhMobilitasSkor(
  items?: Partial<RisikoJatuhItems> | null
): number {
  const src = items || {};
  const jumlah = RISIKO_JATUH_MOBILITAS_KEYS.reduce(
    (sum, key) => sum + num((src as any)[key]),
    0
  );
  return jumlah >= 4 ? 7 : 0;
}

export function risikoJatuhTotal(items?: Partial<RisikoJatuhItems> | null): number {
  const src = items || {};
  const biner = RISIKO_JATUH_BINER_KEYS.reduce(
    (sum, key) => sum + num((src as any)[key]),
    0
  );
  return biner + risikoJatuhMobilitasSkor(src);
}

export function risikoJatuhLengkap(
  items?: Partial<RisikoJatuhItems> | null
): boolean {
  const src = items || {};
  return RISIKO_JATUH_KEYS.every(key => terisi((src as any)[key]));
}

export function risikoJatuhKategori(
  items?: Partial<RisikoJatuhItems> | null
): string {
  if (!risikoJatuhLengkap(items)) return "";
  const total = risikoJatuhTotal(items);
  if (total >= 17) return "Risiko Tinggi (≥17)";
  if (total >= 6) return "Risiko Sedang (6–16)";
  return "Risiko Rendah (0–5)";
}

export function risikoJatuhColor(items?: Partial<RisikoJatuhItems> | null): string {
  if (!risikoJatuhLengkap(items)) return "blue-grey";
  const total = risikoJatuhTotal(items);
  if (total >= 17) return "red";
  if (total >= 6) return "amber darken-2";
  return "green";
}

export interface TekananDarahPasangan {
  td_berbaring_sistolik: number | null;
  td_berbaring_diastolik: number | null;
  td_berdiri_sistolik: number | null;
  td_berdiri_diastolik: number | null;
}

export function ortostatikSaran(tv?: Partial<TekananDarahPasangan> | null): YaTidak | null {
  const src = tv || {};
  const nilai = [
    src.td_berbaring_sistolik,
    src.td_berbaring_diastolik,
    src.td_berdiri_sistolik,
    src.td_berdiri_diastolik
  ];
  if (nilai.some(v => !terisi(v))) return null;

  const turunSistolik = num(src.td_berbaring_sistolik) - num(src.td_berdiri_sistolik);
  const turunDiastolik = num(src.td_berbaring_diastolik) - num(src.td_berdiri_diastolik);

  return turunSistolik > 20 || turunDiastolik > 10 ? "ya" : "tidak";
}

export function ortostatikSelisih(
  tv?: Partial<TekananDarahPasangan> | null
): { sistolik: number; diastolik: number } {
  const src = tv || {};
  return {
    sistolik: num(src.td_berbaring_sistolik) - num(src.td_berdiri_sistolik),
    diastolik: num(src.td_berbaring_diastolik) - num(src.td_berdiri_diastolik)
  };
}
